import type { ReceivableBucket, ReceivableListItem } from "@/lib/pendingOrders";
import {
  getReceivableExpiresAtMs,
  isReceivableAnnouncementItem,
  isReceivableSlipItem,
} from "@/lib/pendingOrders";

/** Thai labels shown as a single status badge in receivable tables. */
export type ReceivableDisplayStatus =
  | "ลบรายการ"
  | "หมดอายุ"
  | "เหลือรอจัดสรร"
  | "รับบางส่วน"
  | "รอตรวจ"
  | "ค้าง";

/** Filter dropdown label for dismissed items (noun phrase). */
export const RECEIVABLE_DISMISSED_FILTER_LABEL = "รายการที่ลบ";

const BUCKET_TO_LABEL: Record<ReceivableBucket, ReceivableDisplayStatus> = {
  dismissed: "ลบรายการ",
  expired: "หมดอายุ",
  review: "รอตรวจ",
  pending: "ค้าง",
};

function isCountdownExpired(item: ReceivableListItem): boolean {
  const expiresMs = getReceivableExpiresAtMs(item);
  return expiresMs != null && expiresMs <= Date.now();
}

function parseAmountField(
  item: ReceivableListItem,
  keys: string[],
): number | null {
  for (const key of keys) {
    const raw = item.fields.find((field) => field.key === key)?.value?.trim();
    if (!raw || raw === "—") continue;
    const cleaned = raw.replace(/[^\d.-]/g, "");
    if (!cleaned) continue;
    const num = Number(cleaned);
    if (Number.isFinite(num)) return num;
  }
  if (item.amountLabel) {
    const cleaned = item.amountLabel.replace(/[^\d.-]/g, "");
    const num = Number(cleaned);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function hasPartialPayment(item: ReceivableListItem): boolean {
  if (!isReceivableAnnouncementItem(item)) return false;
  const paid = parseAmountField(item, ["amount_paid"]);
  const remaining = parseAmountField(item, [
    "amount",
    "amount_remaining",
  ]);
  if (paid != null && paid > 0.009 && remaining != null && remaining > 0.009) {
    return true;
  }
  return false;
}

function hasUnallocatedRemainder(item: ReceivableListItem): boolean {
  if (!isReceivableSlipItem(item)) return false;
  const allocated = parseAmountField(item, ["amount_allocated"]);
  const remaining = parseAmountField(item, [
    "amount_remaining",
    "amount",
    "slip_amount",
  ]);
  if (
    allocated != null &&
    allocated > 0.009 &&
    remaining != null &&
    remaining > 0.009
  ) {
    return true;
  }
  return false;
}

/**
 * Approach 2: one status per row (table badge).
 * Priority: ลบรายการ > หมดอายุ > เหลือรอจัดสรร > รับบางส่วน > รอตรวจ > ค้าง
 */
export function resolveReceivableDisplayStatus(
  item: ReceivableListItem,
): ReceivableDisplayStatus {
  if (item.statuses.includes("dismissed")) return "ลบรายการ";
  if (item.statuses.includes("expired") || isCountdownExpired(item)) {
    return "หมดอายุ";
  }
  if (hasUnallocatedRemainder(item)) return "เหลือรอจัดสรร";
  if (hasPartialPayment(item)) return "รับบางส่วน";
  if (item.statuses.includes("review")) return "รอตรวจ";
  if (item.statuses.includes("pending")) return "ค้าง";
  return BUCKET_TO_LABEL[item.statuses[0] ?? "pending"] ?? "ค้าง";
}

export function receivableMatchesStatusFilter(
  item: ReceivableListItem,
  statusLabel: string,
): boolean {
  const display = resolveReceivableDisplayStatus(item);
  if (statusLabel === RECEIVABLE_DISMISSED_FILTER_LABEL) {
    return display === "ลบรายการ";
  }
  return display === statusLabel;
}

/** Soft-dismissed / TTL-expired — hidden from default table until filtered. */
export function isReceivableArchivedDisplayStatus(
  item: ReceivableListItem,
): boolean {
  const status = resolveReceivableDisplayStatus(item);
  return status === "ลบรายการ" || status === "หมดอายุ";
}

/**
 * Candidates for match / move / resplit pickers.
 * Excludes dismissed + expired so money ops match the default tab list.
 */
export function isReceivableUsableMoneyCandidate(
  item: ReceivableListItem,
): boolean {
  return !isReceivableArchivedDisplayStatus(item);
}

export function filterReceivableMoneyCandidates(
  items: ReceivableListItem[],
): ReceivableListItem[] {
  return items.filter(isReceivableUsableMoneyCandidate);
}
